import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Input from '@/components/Inputs/Input';
import Typo from '@/components/Typography/Typo';
import SFilter from '@/features/StructuredFilter/class/SFilter';
import InlineKeywordFilterV2 from '@/features/StructuredFilter/components/InlineKeywordFilterV2';
import useStructuredFilterQuery from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import useStructuredFilter from '@/hooks/domain/App/useStructuredFilter';
import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import type { TOPIC_2_TYPE } from '@/types/taxonomyConst';

type Props = Topic2CreateParams<TOPIC_2_TYPE.RULE> & {
  index: number;
  previewLoading?: boolean;
  focused: boolean;
  onTitleChange: (index: number, name: string) => string;
  onRuleChange: (index: number, rule?: FilterRule) => void;
  onDeleteClick: (index: number) => void;
  onSeePreviewClick: (index: number) => void;
};

// 두 관심사(keyword filter + structured filter)를 합쳐서 최종 StructuredFilter 생성
export const buildFullFilter = (
  kw: StructuredFilter,
  formats: StructuredFilterQueryFormatT[],
): StructuredFilter | undefined => {
  const dataFilter = new SFilter();
  dataFilter.addFilterQueryFormats(formats);
  // keyword(FEEDBACK_TEXT)는 excludeDataFilterKeys에 포함되어 키 충돌 없음
  return new SFilter({ ...(dataFilter.get() ?? {}), ...kw }).get();
};

const RuleTopicCandidateItem = (props: Props) => {
  // keyword(FEEDBACK_TEXT) 전용 상태
  const [keywordFilter, setKeywordFilter] = useState<StructuredFilter>(() => {
    const textFilters = new SFilter(props.filter_rule).getSinlgeDataFilter(
      FEEDBACK_COLUMNS.FEEDBACK_TEXT,
    );
    if (textFilters.length === 0) return {};
    return { [FEEDBACK_COLUMNS.FEEDBACK_TEXT]: textFilters } as StructuredFilter;
  });

  const { parseStructuredFilter } = useStructuredFilter();

  // structured filter(데이터 필터) 전용 — callback 없이 컴포넌트에서 직접 합성
  const { filterQueryFormats, onFilterSetChange, clearFilter } =
    useStructuredFilterQuery(
      undefined,
      parseStructuredFilter(new SFilter(props.filter_rule).getDataFilterMap()),
    );

  // StructuredFilter 변경 → hook state 업데이트 + 부모 통지
  const handleStructuredFilterChange = (
    formats: StructuredFilterQueryFormatT[],
  ) => {
    onFilterSetChange(formats);
    props.onRuleChange(props.index, buildFullFilter(keywordFilter, formats));
  };

  // Clear filter → hook state 초기화 + keyword는 유지
  const handleClearFilter = () => {
    clearFilter();
    props.onRuleChange(props.index, buildFullFilter(keywordFilter, []));
  };

  // Keyword 변경 → keyword state 업데이트 + 부모 통지
  const onIncludeKeywordChange = (
    newKeywordFilter: StructuredFilterStringNumberValueSet | null,
  ) => {
    const updated = new SFilter(keywordFilter);
    updated.setIncludeTextFilter(newKeywordFilter);
    const newKw = updated.get() ?? {};
    setKeywordFilter(newKw);
    props.onRuleChange(props.index, buildFullFilter(newKw, filterQueryFormats));
  };

  const onExcludeKeywordChange = (
    newKeywordFilter: StructuredFilterStringNumberValueSet | null,
  ) => {
    const updated = new SFilter(keywordFilter);
    updated.setExcludeTextFilter(newKeywordFilter);
    const newKw = updated.get() ?? {};
    setKeywordFilter(newKw);
    props.onRuleChange(props.index, buildFullFilter(newKw, filterQueryFormats));
  };

  // keywordFilter에서 직접 파생 (저비용 계산)
  const kwSFilter = new SFilter(keywordFilter);
  const filterIncludeTextFilter = kwSFilter.getIncludeTextFilter();
  const filterExcludeTextFilter = kwSFilter.getExcludeTextFilter();

  return (
    <Style g={24} focused={props.focused}>
      <VFlex g={44}>
        <Flex j="space-between" a="flex-end">
          <Input
            sizeType="l"
            css={inputCss}
            defaultValue={props.name}
            placeholder="type topic name"
            onBlur={(e) => {
              const newValue = props.onTitleChange(props.index, e.target.value);
              e.target.value = newValue;
            }}
          />
        </Flex>
        <VFlex g={24}>
          <SFilterRow>
            <Flex j="space-between" a="center" style={{ width: '100%' }}>
              <SLabelText type="label2" weight="sb">
                Filter
              </SLabelText>
              <SLabelText>Match with all filter</SLabelText>
            </Flex>
          </SFilterRow>
          <VFlex g={16}>
            <SFilterRow>
              <SLabelText>Feedback filter</SLabelText>
              <StructuredFilter
                values={filterQueryFormats}
                onChange={handleStructuredFilterChange}
                clearFilter={handleClearFilter}
                disAllowFilter={[FEEDBACK_COLUMNS.TAXONOMY_TOPIC]}
              />
            </SFilterRow>
            <SFilterRow>
              <SLabelText>Include keywords</SLabelText>
              <InlineKeywordFilterV2
                name={`topic-include-keyword-filter-${props.index}`}
                keywordFilter={filterIncludeTextFilter}
                onChange={onIncludeKeywordChange}
                allowSpace
                restriction="include"
              />
            </SFilterRow>
            <SFilterRow>
              <SLabelText>Exclude keywords</SLabelText>
              <InlineKeywordFilterV2
                name={`topic-exclude-keyword-filter-${props.index}`}
                keywordFilter={filterExcludeTextFilter}
                onChange={onExcludeKeywordChange}
                allowSpace
                restriction="exclude"
              />
            </SFilterRow>
          </VFlex>
        </VFlex>
      </VFlex>
      <Flex j="space-between" a="center">
        <Button
          variant="ghost"
          sizeType="m"
          onClick={() => props.onDeleteClick(props.index)}
        >
          Delete this topic
        </Button>
        <Button
          variant="ghost"
          sizeType="m"
          onClick={() => props.onSeePreviewClick(props.index)}
          disabled={props.previewLoading}
        >
          Update preview
        </Button>
      </Flex>
    </Style>
  );
};

type StyleProps = {
  focused: boolean;
};

const Style = styled(VFlex)<StyleProps>`
  padding: 24px;
  background-color: ${(props) => props.theme.colors.gray200};
  border-radius: ${(props) => props.theme.radius.card}px;
  border: 1px solid transparent;
  transition: all 0.2s;
  ${({ focused }) =>
    focused &&
    css`
      border-color: ${(props) => props.theme.colors.primary100};
    `}
`;

const SLabelText = styled(Typo)`
  color: ${(props) => props.theme.colors.gray600};
  flex-shrink: 0;
`;

const SFilterRow = styled(VFlex)`
  align-items: flex-start;
  gap: 8px;
`;

const inputCss = css`
  background-color: ${(props) => props.theme.colors.gray100};
`;

export default RuleTopicCandidateItem;
